import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Calendar } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

interface MentorAvailabilityToggleProps {
  isAvailable: boolean;
  isLoading?: boolean;
}

export function MentorAvailabilityToggle({ isAvailable, isLoading }: MentorAvailabilityToggleProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const updateAvailability = useMutation({
    mutationFn: async (newAvailability: boolean) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      const { error } = await supabase
        .from("mentor_profiles")
        .update({ 
          is_available_for_mentorship: newAvailability,
          availability_updated_at: new Date().toISOString()
        })
        .eq("id", user.id);

      if (error) throw error;
      return newAvailability;
    },
    onMutate: async (newAvailability) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["mentor_profile"] });
      
      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData(["mentor_profile", user?.id]);
      
      // Optimistically update
      queryClient.setQueryData(["mentor_profile", user?.id], (old: any) => ({
        ...old,
        is_available_for_mentorship: newAvailability,
      }));
      
      return { previousProfile };
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousProfile) {
        queryClient.setQueryData(["mentor_profile", user?.id], context.previousProfile);
      }
      toast.error("Failed to update availability: " + error.message);
    },
    onSuccess: (newAvailability) => {
      toast.success(newAvailability 
        ? "You are now available for mentorship requests" 
        : "Mentorship availability paused"
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["mentor_profile"] });
    },
  });

  const handleToggle = (checked: boolean) => {
    updateAvailability.mutate(checked);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Mentorship Availability
        </CardTitle>
        <CardDescription>
          Control whether you receive new mentorship requests
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="mentor-availability" className="text-sm font-medium">
              Available for Mentorship
            </Label>
            <p className="text-xs text-muted-foreground">
              {isAvailable 
                ? "You may receive mentorship requests that match your selected topics."
                : "You will not receive new mentorship requests while unavailable."
              }
            </p>
          </div>
          <Switch
            id="mentor-availability"
            checked={isAvailable}
            onCheckedChange={handleToggle}
            disabled={updateAvailability.isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}
